import React, { useEffect, useRef, useState } from 'react';
import { FileText, Loader2, AlertCircle, ClipboardList } from 'lucide-react';
import {
  TabContentWrapper,
  ExtractionHeader,
  ExtractionTitle,
  ExtractedBadge,
  ExtractionList,
  ExtractionItem,
  ItemSource,
  ItemSourceDoc,
  ItemSourcePage,
  ItemSourceDot,
  ItemTitle,
  ExtractionFieldGrid,
  ExtractionField,
  FieldLabel,
  FieldValue,
  StatusMessage,
  ExtractButton,
  LoadingState,
  EmptyExtractionState,
  ExtractionTabsWrapper,
  ExtractionTabItem,
} from './processing.styles';

import { useExtraction } from './hook/Useprocessing';

/*
  Rewritten per the new flow:
   1. On mount, hit extractionDocuments(listId) directly — no separate
      status pre-check hook.
   2. If the response is "Extraction Complete!", hit getExtractionData(listId)
      and render it.
   3. If not, show the pending/in-progress state with a "Check Again" button
      that re-hits extractionDocuments(listId) — repeat until it reports
      complete.
*/

const Extraction = ({ listId }) => {
  const { extractMutation, extractionQuery, isComplete, trigger } = useExtraction(listId);

  const [activeTabId, setActiveTabId] = useState(null);
  const observerRef = useRef(null);

  useEffect(() => {
    if (listId) trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId]);

  const extractions = extractionQuery.data?.data ?? [];
  const totalItems = extractions.reduce((acc, e) => acc + (e.fileData?.length ?? 0), 0);

  useEffect(() => {
    if (extractions.length === 0) return;

    setActiveTabId(extractions[0]._id);
    observerRef.current?.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {
          const id = visible[0].target.getAttribute('data-extraction-id');
          if (id) setActiveTabId(id);
        }
      },
      { rootMargin: '0px 0px -60% 0px', threshold: 0 }
    );

    extractions.forEach((e) => {
      const el = document.getElementById(`extraction-section-${e._id}`);
      if (el) observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [extractions]);

  const handleTabClick = (id) => {
    setActiveTabId(id);
    const el = document.getElementById(`extraction-section-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCheckAgain = () => trigger();

  // ── First call in flight, nothing to show yet ────────────────────────────
  if (extractMutation.isPending && !extractionQuery.data) {
    return (
      <TabContentWrapper>
        <LoadingState>
          <Loader2 size={22} className="spin" />
          <span>Extracting documents…</span>
        </LoadingState>
      </TabContentWrapper>
    );
  }

  // ── extractionDocuments() failed outright ────────────────────────────────
  if (extractMutation.isError && !extractionQuery.data) {
    return (
      <TabContentWrapper>
        <EmptyExtractionState>
          <AlertCircle size={36} strokeWidth={1.2} />
          <p style={{ margin: 0, fontWeight: 500 }}>Extraction failed</p>
          <p style={{ margin: 0, fontSize: 13 }}>
            {extractMutation.error?.message || 'Something went wrong. Please try again.'}
          </p>
          <ExtractButton type="button" onClick={handleCheckAgain}>
            <FileText size={14} /> Try Again
          </ExtractButton>
        </EmptyExtractionState>
      </TabContentWrapper>
    );
  }

  // ── Fetching the actual extraction data once marked complete ────────────
  if (isComplete && extractionQuery.isLoading) {
    return (
      <TabContentWrapper>
        <LoadingState>
          <Loader2 size={22} className="spin" />
          <span>Fetching extracted data…</span>
        </LoadingState>
      </TabContentWrapper>
    );
  }

  // ── Data arrived ──────────────────────────────────────────────────────────
  if (extractions.length > 0) {
    return (
      <TabContentWrapper>
        <ExtractionHeader>
          <ExtractionTitle>Extraction Result</ExtractionTitle>
          <ExtractedBadge>
            <FileText size={14} />
            {totalItems} Items Extracted
          </ExtractedBadge>
        </ExtractionHeader>

        <ExtractionTabsWrapper>
          {extractions.map((extraction) => (
            <ExtractionTabItem
              key={extraction._id}
              $active={activeTabId === extraction._id}
              onClick={() => handleTabClick(extraction._id)}
              type="button"
              aria-label={`Scroll to ${extraction.documentName}`}
            >
              {extraction.documentName ?? extraction.name}
            </ExtractionTabItem>
          ))}
        </ExtractionTabsWrapper>

        <ExtractionList>
          {extractions.map((extraction) => (
            <ExtractionItem
              key={extraction._id}
              id={`extraction-section-${extraction._id}`}
              data-extraction-id={extraction._id}
            >
              <ItemSource>
                <ItemSourceDoc>
                  <FileText size={12} style={{ flexShrink: 0 }} />
                  {extraction.documentName ?? extraction.name}
                </ItemSourceDoc>
                {extraction.createdAt && (
                  <>
                    <ItemSourceDot>•</ItemSourceDot>
                    <ItemSourcePage>
                      {new Date(extraction.createdAt).toLocaleDateString('en-AU', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </ItemSourcePage>
                  </>
                )}
              </ItemSource>

              <ItemTitle>{extraction.documentName ?? extraction.name}</ItemTitle>

              <ExtractionFieldGrid>
                {extraction.fileData?.map((field) => (
                  <ExtractionField key={field.label ?? field.title}>
                    <FieldLabel>{field.title}</FieldLabel>
                    <FieldValue>{field.value ?? field.extractedValue}</FieldValue>
                  </ExtractionField>
                ))}
              </ExtractionFieldGrid>
            </ExtractionItem>
          ))}
        </ExtractionList>
      </TabContentWrapper>
    );
  }

  // ── Not complete yet — pending state, "Check Again" re-triggers ─────────
  if (!isComplete) {
    return (
      <TabContentWrapper>
        <EmptyExtractionState>
          <ClipboardList size={36} strokeWidth={1.2} />
          <p style={{ margin: 0, fontWeight: 500 }}>Extraction In Progress</p>
          <p style={{ margin: 0, fontSize: 13 }}>
            Your documents are still being extracted. Check back shortly or try again.
          </p>
          <ExtractButton
            type="button"
            onClick={handleCheckAgain}
            disabled={extractMutation.isPending}
          >
            {extractMutation.isPending ? (
              <>
                <Loader2 size={14} className="spin" /> Checking…
              </>
            ) : (
              <>
                <FileText size={14} /> Check Again
              </>
            )}
          </ExtractButton>
        </EmptyExtractionState>
      </TabContentWrapper>
    );
  }

  // ── Query-level error ─────────────────────────────────────────────────────
  if (extractionQuery.isError) {
    return (
      <TabContentWrapper>
        <StatusMessage $type="error" style={{ margin: '1.5rem 0' }}>
          <AlertCircle size={14} />
          {extractionQuery.error?.message || 'Failed to load extraction data.'}
        </StatusMessage>
      </TabContentWrapper>
    );
  }

  // ── Complete but empty ────────────────────────────────────────────────────
  return (
    <TabContentWrapper>
      <EmptyExtractionState>
        <ClipboardList size={36} strokeWidth={1.2} />
        <p style={{ margin: 0 }}>No extraction data available yet.</p>
      </EmptyExtractionState>
    </TabContentWrapper>
  );
};

export default Extraction;
